import {
  Checkbox,
  FormControl,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { mdiAlert } from '@mdi/js';
import Icon from '@mdi/react';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AssignmentsSelect from 'scenes/WorkDemo/components/RecordDialog/AssignmentsSelect';
import { selectProjectAssignments } from 'store/assignments/selectors/assignment.selectors';
import { selectIsShadowProject } from 'store/projects/selectors/projectsSelectors';
import { setClockOutRecordAttributes } from 'store/records/actions/records.actions';
import { selectedClockOutRecordWithUpdatedAttributes } from 'store/records/selectors/records.selectors';
import { selectCurrentUserActiveEmployerProject } from 'store/resources/selectors/resources.selectors';
import { selectShadowRecordForRecord } from 'store/shadowData/selectors/shadowData.selectors';
import { selectUserActiveEmployerBusinessId } from 'store/user/selectors.js/user.selectors';
import getNestedAssignmentNumber from 'utils/getNestedAssigmentNumber';
import ImageSelectComponent from './ImageSelectComponent';
import WorkTypeSelect from './WorkTypeSelect';

const useStyles = makeStyles((theme) => ({
  container: {
    ...theme.component.dialogPaper,
    padding: '8px',
    borderRadius: '2px',
    opacity: '0.9',
  },
  gridItem: {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
}));

export function ClockOutInputs({
  projectId,
  projectType,
  setImageFiles,
  isClockOutPending,
  assignmentRequired,
  recordId,
  onShadowAssignmentChange,
}) {
  const classes = useStyles();

  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const debounceDescriptionSearch = useCallback(
    debounce(setDescription, 500),
    []
  );

  const [unitAmountDialogOpen, setUnitAmountDialogOpen] = useState(false);

  const setAssignmentUnitAmount = (value) => {
    dispatch(setClockOutRecordAttributes({ assignmentUnitAmount: value }));
  };
  const setAssignmentUnitAmountDialogOpen = (value) => {
    setUnitAmountDialogOpen(value);
  };
  function setDescription(value) {
    dispatch(setClockOutRecordAttributes({ description: value }));
  }

  const setIsProjectCompletedSelected = (value) => {
    dispatch(
      setClockOutRecordAttributes({
        projectCompleted: value,
      })
    );
  };

  const projectAssignments = useSelector((state) =>
    selectProjectAssignments(state, projectId)
  );

  const clockOutDialogAttributes = useSelector(
    selectedClockOutRecordWithUpdatedAttributes
  );

  const isShadowProject = useSelector(selectIsShadowProject(projectId));
  const activeEmployerBusinessId = useSelector(
    selectUserActiveEmployerBusinessId
  );
  const activeEmployerProject = useSelector(
    selectCurrentUserActiveEmployerProject
  );

  const shadowProjectId = isShadowProject
    ? `${projectId}_${activeEmployerBusinessId}`
    : null;

  const shadowProjectAssignments = useSelector((state) =>
    shadowProjectId ? selectProjectAssignments(state, shadowProjectId) : []
  );

  const hasVisibleShadowAssignments = shadowProjectAssignments.some(
    (assignment) => assignment.hideInClocking !== true
  );

  const shadowRecord = useSelector((state) => {
    if (!isShadowProject || !recordId || !activeEmployerBusinessId)
      return undefined;
    return selectShadowRecordForRecord(
      recordId,
      activeEmployerBusinessId
    )(state);
  });

  const [shadowAssignment, setShadowAssignment] = useState(null);
  const [shadowAssignmentUnitAmount, setShadowAssignmentUnitAmount] =
    useState(undefined);
  const [shadowUnitAmountDialogOpen, setShadowUnitAmountDialogOpen] =
    useState(false);

  const reportShadowAssignmentChange = (assignment, unitAmount) => {
    if (onShadowAssignmentChange) {
      onShadowAssignmentChange({
        assignment,
        assignmentUnitAmount: unitAmount,
      });
    }
  };

  useEffect(() => {
    if (shadowRecord?.assignment) {
      setShadowAssignment(shadowRecord.assignment);
      setShadowAssignmentUnitAmount(shadowRecord.assignmentUnitAmount);
      reportShadowAssignmentChange(
        shadowRecord.assignment,
        shadowRecord.assignmentUnitAmount
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shadowRecord]);

  const handleShadowAssignmentChange = (assignmentId) => {
    const newAssignment =
      shadowAssignment === assignmentId ? null : assignmentId;
    setShadowAssignment(newAssignment);
    setShadowAssignmentUnitAmount(undefined);
    reportShadowAssignmentChange(newAssignment, undefined);
  };

  const handleShadowAssignmentUnitAmountChange = (value) => {
    setShadowAssignmentUnitAmount(value);
    reportShadowAssignmentChange(shadowAssignment, value);
  };

  const handleTypeSelect = (value) => {
    dispatch(setClockOutRecordAttributes({ type: value }));
  };

  const handleAssignmentChange = (assignmentId) => {
    // handling assignment deselect on click if assignment already selected
    if (assignmentId !== clockOutDialogAttributes.assignment) {
      dispatch(
        setClockOutRecordAttributes({
          assignment: assignmentId,
          assignmentUnitAmount: undefined,
        })
      );
    } else {
      dispatch(
        setClockOutRecordAttributes({
          assignment: undefined,
          assignmentUnitAmount: undefined,
        })
      );
    }
  };

  const selectedAssignment = projectAssignments.find(
    (assigmnet) => assigmnet.id === clockOutDialogAttributes.assignment
  );

  const isSelectedAssignment =
    clockOutDialogAttributes.assignment && selectedAssignment;

  const disabled = isClockOutPending;

  const assigmentNumber = isSelectedAssignment
    ? getNestedAssignmentNumber(selectedAssignment, projectAssignments)
    : undefined;

  return (
    <>
      <Grid container className={classes.container}>
        <Grid item xs={12}>
          <WorkTypeSelect
            type={clockOutDialogAttributes.type}
            handleTypeSelect={handleTypeSelect}
            disabled={disabled}
          ></WorkTypeSelect>
        </Grid>
        <Grid item xs={12} className={classes.gridItem}>
          <TextField
            InputLabelProps={{ shrink: true }}
            fullWidth
            id={'description'}
            label={t('calendar.description')}
            variant="outlined"
            size="small"
            defaultValue={clockOutDialogAttributes.description}
            type="text"
            onChange={(e) => debounceDescriptionSearch(e.target.value)}
            multiline
            disabled={disabled}
          />
        </Grid>
        {isSelectedAssignment ? (
          <Grid item xs={12} className={classes.gridItem}>
            <Grid container spacing={2}>
              <Grid item xs={12} className={classes.gridItem}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  id={'assignment'}
                  label={t('calendar.selected_assignment')}
                  variant="outlined"
                  size="small"
                  type="text"
                  disabled={true}
                  value={`${assigmentNumber ? assigmentNumber : ''} ${
                    selectedAssignment.name
                  }`}
                />
              </Grid>
              <Grid item xs={6} className={classes.gridItem}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  id={'assignment-unitAmountGoal'}
                  label={t('common.total_work')}
                  variant="outlined"
                  size="small"
                  type="text"
                  disabled={true}
                  value={`${
                    selectedAssignment.unitAmountGoal
                      ? selectedAssignment.unitAmountGoal
                      : ''
                  } ${selectedAssignment.unit ? selectedAssignment.unit : ''}`}
                />
              </Grid>
              <Grid item xs={6} className={classes.gridItem}>
                <TextField
                  onChange={(event) =>
                    setAssignmentUnitAmount(event.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <p>
                          {selectedAssignment?.unit
                            ? selectedAssignment.unit
                            : ''}
                        </p>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  id={'assignment-unitAmountGoal'}
                  label={t('calendar.work_done_today')}
                  variant="outlined"
                  size="small"
                  type="text"
                  disabled={
                    !selectedAssignment?.unitAmountGoal &&
                    !selectedAssignment?.unit
                  }
                  value={
                    clockOutDialogAttributes.assignmentUnitAmount
                      ? clockOutDialogAttributes.assignmentUnitAmount
                      : ''
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        ) : null}
        <Grid item xs={12} className={classes.gridItem}>
          <ImageSelectComponent
            setImageFiles={setImageFiles}
            disabled={disabled}
            projectId={projectId}
          ></ImageSelectComponent>
        </Grid>

        {assignmentRequired && !selectedAssignment ? (
          <Grid
            item
            xs={12}
            style={{
              display: 'flex',
              gap: '10px',
              backgroundColor: '#ff9800',
              borderRadius: '4px',
              padding: '10px',
              marginTop: '20px',
            }}
          >
            <Icon path={mdiAlert} size={1}></Icon>
            <Typography>{t('record.assignment_required')}</Typography>
          </Grid>
        ) : null}

        <Grid item xs={12} className={classes.gridItem}>
          <FormControl fullWidth>
            <AssignmentsSelect
              a1={
                projectAssignments && projectAssignments[0]
                  ? projectAssignments[0]
                  : undefined
              }
              selectedAssignment={clockOutDialogAttributes.assignment}
              assignments={projectAssignments}
              update={handleAssignmentChange}
              unitAmountDialogOpen={unitAmountDialogOpen}
              setDialogOpen={(value) =>
                setAssignmentUnitAmountDialogOpen(value)
              }
              setAssignmentUnitAmount={setAssignmentUnitAmount}
              unitAmount={clockOutDialogAttributes.assignmentUnitAmount}
              disabled={disabled}
            />
          </FormControl>
        </Grid>
        {isShadowProject && hasVisibleShadowAssignments && (
            <Grid item xs={12} className={classes.gridItem}>
              <FormControl fullWidth>
                <AssignmentsSelect
                  label={`${t('expense.assignment')} - ${activeEmployerProject?.name || ''}`}
                  a1={
                    shadowProjectAssignments[0]
                      ? shadowProjectAssignments[0]
                      : undefined
                  }
                  selectedAssignment={shadowAssignment}
                  assignments={shadowProjectAssignments}
                  update={handleShadowAssignmentChange}
                  unitAmountDialogOpen={shadowUnitAmountDialogOpen}
                  setDialogOpen={(value) =>
                    setShadowUnitAmountDialogOpen(value)
                  }
                  setAssignmentUnitAmount={
                    handleShadowAssignmentUnitAmountChange
                  }
                  unitAmount={shadowAssignmentUnitAmount}
                  disabled={disabled}
                />
              </FormControl>
            </Grid>
          )}
        {projectType === 'SHORT_TERM' ? (
          <Grid item xs={12}>
            <Checkbox
              checked={clockOutDialogAttributes.projectCompleted || false}
              onChange={() =>
                setIsProjectCompletedSelected(
                  !clockOutDialogAttributes.projectCompleted
                )
              }
            ></Checkbox>
            {t('record.set_project_as_completed')}
          </Grid>
        ) : null}
      </Grid>
    </>
  );
}
